/* Full-width global header: branding · search · import · theme · identity.

   Zones are anchored (branding left, cluster right, search centred) so that
   disabling search on a non-searchable workspace causes no reflow — the field
   stays put and dims. That's why search is disabled rather than hidden.

   The header knows nothing about which workspaces exist: it receives
   `searchable`/`placeholder` from the shell, which reads them off the active
   workspace's own declaration. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import type { Me, ThemePref } from '../../core/types.js';
import '../../component/g-avatar/g-avatar.js';
import '../../component/g-button/g-button.js';
import '../../component/g-field/g-field.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-mark/g-mark.js';

@customElement('g-header')
export class GHeader extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: flex;
      align-items: center;
      gap: var(--space-4);
      height: var(--header-h);
      flex: none;
      padding: 0 var(--space-4);
      border-bottom: 1px solid var(--border);
      background: var(--surface);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      width: calc(var(--rail-w) - var(--space-4));
      flex: none;
      font-family: var(--font-display);
      font-size: 1.15rem;
      font-weight: 800;
      letter-spacing: -0.01em;
      --mark-size: 18px;
      /* §29: the wordmark performs no action */
      user-select: none;
    }
    .search {
      display: flex;
      flex: 1;
      max-width: 560px;
      margin-inline: auto;
    }
    .cluster {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      flex: none;
      margin-left: auto;
    }
    .queue {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      padding: 3px var(--space-2);
      border-radius: var(--radius-full);
      background: color-mix(in oklab, var(--accent) 18%, transparent);
      color: var(--accent);
      font-family: var(--font-mono);
      font-size: 0.75rem;
    }
    g-avatar {
      --avatar-size: 28px;
    }
  `];

  @property({ type: Boolean }) searchable = false;
  @property() placeholder = '';
  @property() query = '';
  /** In-flight imports (pending + processing). Zero hides the indicator entirely. */
  @property({ type: Number }) queueCount = 0;
  @property({ attribute: false }) me: Me | null = null;
  @property() theme: ThemePref = 'auto';

  override render() {
    return html`
      <div class="brand"><g-mark></g-mark>grain</div>

      <div class="search">
        <g-field
          .value=${this.query}
          .disabled=${!this.searchable}
          placeholder=${this.searchable ? this.placeholder : 'Search'}
        ></g-field>
      </div>

      <div class="cluster">
        <!-- §23: only actionable info. No idle "0". -->
        ${this.queueCount > 0
          ? html`<span
              class="queue"
              title="${this.queueCount} import${this.queueCount === 1 ? '' : 's'} in progress"
            >
              <g-icon name="import" style="--icon-size:13px"></g-icon>${this.queueCount}
            </span>`
          : ''}

        <g-button
          icon-only
          label=${`Theme: ${this.theme}`}
          @click=${() =>
            this.dispatchEvent(
              new CustomEvent('toggle-theme', { bubbles: true, composed: true }),
            )}
        >
          <g-icon name=${`theme-${this.theme}`}></g-icon>
        </g-button>

        <g-avatar .me=${this.me} title=${this.me?.email ?? 'Not signed in'}></g-avatar>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-header': GHeader;
  }
}
